import {type EmployeeDto} from '@accumulus/shared';
import {create} from 'zustand';

const STORAGE_KEY = 'employee';

const read = (): EmployeeDto | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as EmployeeDto) : null;
  } catch {
    return null;
  }
};

interface EmployeeState {
  employee: EmployeeDto | null;
  setEmployee: (employee: EmployeeDto | null) => void;
  clearEmployee: () => void;
}

const useEmployeeStore = create<EmployeeState>()((set) => ({
  employee: read(),
  setEmployee: (employee) => {
    try {
      if (employee) localStorage.setItem(STORAGE_KEY, JSON.stringify(employee));
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      // storage unavailable
    }
    set({employee});
  },
  clearEmployee: () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // storage unavailable
    }
    set({employee: null});
  }
}));

/**
 * The signed-in employee. `null` for an old session that has no stored
 * employee; the store re-reads localStorage because the header clears it
 * directly on logout.
 */
export const useEmployee = (): EmployeeDto | null => {
  const employee = useEmployeeStore((s) => s.employee);
  if (employee) return employee;
  return read();
};

/** Role of the signed-in employee, or `null` when unknown. */
export const useEmployeeRole = (): string | null =>
  useEmployee()?.role ?? null;

export const setEmployee = (employee: EmployeeDto | null) =>
  useEmployeeStore.getState().setEmployee(employee);

export const clearEmployee = () => useEmployeeStore.getState().clearEmployee();

export default useEmployeeStore;
